/**
 * Coach tips — professional 2D canvas illustrations.
 *
 * Hand rendering is tip-anchored: each fingertip is placed exactly on the
 * centre of its assigned white key, then knuckles/palm are derived from the
 * tips, so finger-to-key mapping can never drift. Rendering aims for a
 * realistic top-down photo look (staggered contact depths, nails, creases,
 * soft cast shadow) rather than a cartoon.
 */

import type { CoachLesson, CoachSceneId } from "@piano-tutor/shared";

const INK = "#e8f7f1";
const MUTED = "rgba(200, 226, 216, 0.78)";
const ACCENT = "#4dffb0";
const GOLD = "#f0c15a";
const WRONG = "#ff5d6c";

const SKIN_LIGHT = "#f0c39c";
const SKIN_BASE = "#e0a87e";
const SKIN_MID = "#d29a6f";
const SKIN_DEEP = "#b9805a";
const SKIN_EDGE = "rgba(122, 74, 46, 0.35)";
const NAIL = "rgba(246, 218, 192, 0.92)";

const KEY_WHITE_TOP = "#f7f3ea";
const KEY_WHITE_BOT = "#e7e0d1";
const KEY_DOWN_TOP = "#d6ecdc";
const KEY_DOWN_BOT = "#b7dcc4";
const KEY_BLACK = "#171310";
const KEY_BLACK_DOWN = "#2c6a52";
const FELT_RED = "#5a1420";

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function ease(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

function pulse(t: number, period: number): number {
  const p = (t % period) / period;
  return p < 0.5 ? ease(p * 2) : ease((1 - p) * 2);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

interface KeyboardSpec {
  x: number;
  y: number;
  w: number;
  h: number;
  whiteCount: number;
  /** Shift of the C-major black-key pattern so labelled notes look correct. */
  patternOffset?: number;
}

/** One finger of a hand laid on the keyboard, physical order left→right. */
interface FingerSpec {
  /** White-key index under this fingertip (null = floating near palm). */
  key: number | null;
  /** Printed finger number (RH low→high: 1..5, LH low→high: 5..1). */
  num: number;
  press: number;
  highlight: boolean;
  /** Extra tip offset in key widths / px (thumb tuck, hover). */
  ox?: number;
  oy?: number;
}

function hasBlackAfter(i: number): boolean {
  const m = i % 7;
  return m === 0 || m === 1 || m === 3 || m === 4 || m === 5;
}

export class CoachCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private scene: CoachSceneId = "posture";
  private t0 = performance.now();
  private raf = 0;
  private running = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    this.ctx = ctx;
    this.resize();
    window.addEventListener("resize", () => this.resize());
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    const cssW = parent?.clientWidth || 640;
    const cssH = Math.min(400, Math.max(270, cssW * 0.52));
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.canvas.width = Math.floor(cssW * this.dpr);
    this.canvas.height = Math.floor(cssH * this.dpr);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  show(lesson: CoachLesson): void {
    this.scene = lesson.scene;
    this.t0 = performance.now();
    this.start();
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const loop = () => {
      if (!this.running) return;
      this.draw((performance.now() - this.t0) / 1000);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private draw(t: number): void {
    const w = this.canvas.width / this.dpr;
    const h = this.canvas.height / this.dpr;
    const ctx = this.ctx;

    const bg = ctx.createLinearGradient(0, 0, w * 0.15, h);
    bg.addColorStop(0, "#141d19");
    bg.addColorStop(0.55, "#1a2823");
    bg.addColorStop(1, "#0f1512");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    switch (this.scene) {
      case "posture":
        this.scenePosture(w, h, t);
        break;
      case "hand-c-position":
        this.sceneFivePos(w, h, t, false);
        break;
      case "lh-mirror":
        this.sceneFivePos(w, h, t, true);
        break;
      case "direction-ltr-rtl":
        this.sceneDirection(w, h, t);
        break;
      case "thumb-tuck":
        this.sceneThumbTuck(w, h, t);
        break;
      case "black-keys":
        this.sceneBlackKeys(w, h, t);
        break;
      case "chord-shape":
        this.sceneChord(w, h, t);
        break;
      case "blind-focus":
        this.sceneBlind(w, h, t);
        break;
    }

    const vg = ctx.createRadialGradient(w / 2, h * 0.42, h * 0.25, w / 2, h / 2, h * 0.95);
    vg.addColorStop(0, "transparent");
    vg.addColorStop(1, "rgba(3, 8, 10, 0.42)");
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, w, h);
  }

  /* ———— keyboard ———— */

  private kw(spec: KeyboardSpec): number {
    return spec.w / spec.whiteCount;
  }

  private keyCenterX(spec: KeyboardSpec, idx: number): number {
    return spec.x + (idx + 0.5) * this.kw(spec);
  }

  private keyboard(
    spec: KeyboardSpec,
    pressed: Map<number, string> = new Map(),
    blackPressed: Map<number, string> = new Map(),
  ): void {
    const ctx = this.ctx;
    const kw = this.kw(spec);

    // case + red felt strip behind the keys
    ctx.fillStyle = "#241c15";
    ctx.beginPath();
    ctx.roundRect(spec.x - 8, spec.y - 14, spec.w + 16, spec.h + 22, 6);
    ctx.fill();
    ctx.fillStyle = FELT_RED;
    ctx.fillRect(spec.x - 4, spec.y - 5, spec.w + 8, 4);

    for (let i = 0; i < spec.whiteCount; i++) {
      const x = spec.x + i * kw;
      const custom = pressed.get(i);
      const g = ctx.createLinearGradient(0, spec.y, 0, spec.y + spec.h);
      if (custom) {
        g.addColorStop(0, KEY_DOWN_TOP);
        g.addColorStop(1, KEY_DOWN_BOT);
      } else {
        g.addColorStop(0, KEY_WHITE_TOP);
        g.addColorStop(1, KEY_WHITE_BOT);
      }
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.roundRect(x + 0.8, spec.y, kw - 1.6, spec.h, [0, 0, 4, 4]);
      ctx.fill();
      ctx.strokeStyle = "rgba(20, 16, 12, 0.4)";
      ctx.lineWidth = 1;
      ctx.stroke();
      if (custom) {
        ctx.fillStyle = "rgba(45, 150, 105, 0.85)";
        ctx.fillRect(x + 2.5, spec.y + spec.h - 4, kw - 5, 3);
      }
    }

    const bw = kw * 0.58;
    const bh = spec.h * 0.6;
    const off = spec.patternOffset ?? 0;
    for (let i = 0; i < spec.whiteCount - 1; i++) {
      if (!hasBlackAfter(i + off)) continue;
      const x = spec.x + (i + 1) * kw - bw / 2;
      const color = blackPressed.get(i);
      const g = ctx.createLinearGradient(0, spec.y, 0, spec.y + bh);
      g.addColorStop(0, color ? "#3f8a6c" : "#3a332c");
      g.addColorStop(0.12, color ?? KEY_BLACK);
      g.addColorStop(1, color ? "#1e4d3b" : "#0a0806");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.roundRect(x, spec.y, bw, bh, [0, 0, 3, 3]);
      ctx.fill();
    }
  }

  /* ———— hand ———— */

  /** Contact depth (fraction of key length) per physical slot — on the front
   *  of the white keys, below the black-key region. Thumb shallowest. */
  private static RH_DEPTHS = [0.86, 0.72, 0.68, 0.72, 0.78];
  /** Knuckle drop below the keyboard front edge, in key widths (arched hand). */
  private static RH_KNUCKLE_DROP = [0, 0.52, 0.44, 0.52, 0.74];

  /**
   * Lay a hand on the keyboard. `fingers` are in physical left→right order.
   * For the right hand slot 0 is the thumb; for the left hand slot 4 is.
   */
  private handOnKeys(spec: KeyboardSpec, left: boolean, fingers: FingerSpec[]): void {
    const ctx = this.ctx;
    const kw = this.kw(spec);
    const yF = spec.y + spec.h;
    const depths = left ? [...CoachCanvas.RH_DEPTHS].reverse() : CoachCanvas.RH_DEPTHS;
    const drops = left ? [...CoachCanvas.RH_KNUCKLE_DROP].reverse() : CoachCanvas.RH_KNUCKLE_DROP;
    const thumbSlot = left ? 4 : 0;

    const anchorKey = fingers.find((f) => f.key != null)?.key ?? 0;
    const tips = fingers.map((f, i) => {
      const key = f.key ?? anchorKey;
      const x = this.keyCenterX(spec, key) + (f.ox ?? 0) * kw;
      const y =
        spec.y + spec.h * depths[i] + (f.press > 0.1 ? 2.5 : 0) + (f.oy ?? 0) + (f.key == null ? spec.h * 0.5 : 0);
      return { x, y, f };
    });

    const nonThumb = tips.filter((_, i) => i !== thumbSlot);
    const palmCx = nonThumb.reduce((s, t) => s + t.x, 0) / nonThumb.length;

    // knuckles sit just below the keyboard edge, pulled toward the palm centre
    const knuckles = tips.map((tip, i) => {
      if (i === thumbSlot) return null;
      return {
        x: lerp(tip.x, palmCx, 0.24),
        y: yF + kw * drops[i],
      };
    });
    const kxs = knuckles.filter((k): k is { x: number; y: number } => !!k).map((k) => k.x);
    const palmTop = yF + kw * 0.34;
    const palmBottom = palmTop + kw * 2.35;
    const palmL = Math.min(...kxs) - kw * 0.55;
    const palmR = Math.max(...kxs) + kw * 0.55;

    // cast shadow on the keys under the fingers
    ctx.save();
    const sh = ctx.createRadialGradient(palmCx, yF, kw * 0.4, palmCx, yF, kw * 3.2);
    sh.addColorStop(0, "rgba(10, 8, 6, 0.3)");
    sh.addColorStop(1, "transparent");
    ctx.fillStyle = sh;
    ctx.beginPath();
    ctx.ellipse(palmCx, yF + kw * 0.2, kw * 3.1, kw * 1.1, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // forearm (cropped by the canvas bottom, like a photo)
    const armG = ctx.createLinearGradient(palmCx, palmBottom - kw, palmCx, palmBottom + kw * 3);
    armG.addColorStop(0, SKIN_MID);
    armG.addColorStop(1, SKIN_DEEP);
    ctx.fillStyle = armG;
    ctx.beginPath();
    ctx.roundRect(palmCx - kw * 0.95, palmBottom - kw * 0.5, kw * 1.9, kw * 4, kw * 0.7);
    ctx.fill();

    // palm — smooth rounded silhouette from knuckle line down
    const palmG = ctx.createLinearGradient(palmCx, palmTop, palmCx, palmBottom);
    palmG.addColorStop(0, SKIN_BASE);
    palmG.addColorStop(1, SKIN_DEEP);
    ctx.fillStyle = palmG;
    ctx.beginPath();
    ctx.moveTo(palmL, palmTop + kw * 0.55);
    ctx.quadraticCurveTo(palmL - kw * 0.06, palmTop + kw * 0.05, palmL + kw * 0.5, palmTop);
    ctx.quadraticCurveTo(palmCx, palmTop - kw * 0.22, palmR - kw * 0.5, palmTop);
    ctx.quadraticCurveTo(palmR + kw * 0.06, palmTop + kw * 0.05, palmR, palmTop + kw * 0.55);
    ctx.quadraticCurveTo(palmR + kw * 0.02, palmBottom - kw * 0.4, palmR - kw * 0.42, palmBottom - kw * 0.08);
    ctx.quadraticCurveTo(palmCx, palmBottom + kw * 0.28, palmL + kw * 0.42, palmBottom - kw * 0.08);
    ctx.quadraticCurveTo(palmL - kw * 0.02, palmBottom - kw * 0.4, palmL, palmTop + kw * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = SKIN_EDGE;
    ctx.lineWidth = 1.1;
    ctx.stroke();

    // fingers: far side first so nearer fingers overlap naturally
    const drawOrder = left ? [0, 1, 2, 3] : [4, 3, 2, 1];
    for (const i of drawOrder) {
      const k = knuckles[i]!;
      // slight inward bow so the hand reads as gently curved, not robotic
      this.finger(k.x, k.y, tips[i].x, tips[i].y, kw * 0.56, kw * 0.42, tips[i].f, false, 0);
    }

    // thumb — short, angled out of the palm's side edge
    const thumbTip = tips[thumbSlot];
    const thumbBaseX = left ? palmR - kw * 0.12 : palmL + kw * 0.12;
    const thumbBaseY = palmTop + kw * 0.72;
    const bowSign = left ? 1 : -1;
    this.finger(thumbBaseX, thumbBaseY, thumbTip.x, thumbTip.y, kw * 0.66, kw * 0.5, thumbTip.f, true, bowSign);

    // badges after all skin so they read clearly
    for (let i = 0; i < tips.length; i++) {
      this.badge(tips[i].x, tips[i].y, tips[i].f);
    }
  }

  private finger(
    bx: number,
    by: number,
    tx: number,
    ty: number,
    wBase: number,
    wTip: number,
    f: FingerSpec,
    isThumb: boolean,
    bowSign = 0,
  ): void {
    const ctx = this.ctx;
    const dx = tx - bx;
    const dy = ty - by;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L;
    const uy = dy / L;
    const nx = -uy;
    const ny = ux;
    const bow = isThumb ? L * 0.14 * bowSign : 0;
    const mx = (bx + tx) / 2 + nx * bow;
    const my = (by + ty) / 2 + ny * bow;
    const wm = (wBase + wTip) / 2;

    const thN = Math.atan2(ny, nx);

    ctx.beginPath();
    ctx.moveTo(bx + (nx * wBase) / 2, by + (ny * wBase) / 2);
    ctx.quadraticCurveTo(mx + (nx * wm) / 2, my + (ny * wm) / 2, tx + (nx * wTip) / 2, ty + (ny * wTip) / 2);
    ctx.arc(tx, ty, wTip / 2, thN, thN - Math.PI, true);
    ctx.quadraticCurveTo(mx - (nx * wm) / 2, my - (ny * wm) / 2, bx - (nx * wBase) / 2, by - (ny * wBase) / 2);
    ctx.closePath();

    const g = ctx.createLinearGradient(bx, by, tx, ty);
    g.addColorStop(0, SKIN_BASE);
    g.addColorStop(0.55, SKIN_MID);
    g.addColorStop(1, SKIN_LIGHT);
    ctx.fillStyle = g;
    ctx.fill();
    ctx.strokeStyle = SKIN_EDGE;
    ctx.lineWidth = 1.1;
    ctx.stroke();

    // joint creases
    ctx.strokeStyle = "rgba(140, 88, 56, 0.3)";
    ctx.lineWidth = 1;
    for (const s of isThumb ? [0.55] : [0.42, 0.7]) {
      const cx1 = lerp(bx, tx, s) + nx * bow * 0.6;
      const cy1 = lerp(by, ty, s) + ny * bow * 0.6;
      const cw = lerp(wBase, wTip, s) * 0.36;
      ctx.beginPath();
      ctx.moveTo(cx1 + nx * cw, cy1 + ny * cw);
      ctx.quadraticCurveTo(cx1 + ux * 2, cy1 + uy * 2, cx1 - nx * cw, cy1 - ny * cw);
      ctx.stroke();
    }

    // nail
    const nailD = wTip * 0.16;
    const nlX = tx - ux * nailD;
    const nlY = ty - uy * nailD;
    ctx.save();
    ctx.translate(nlX, nlY);
    ctx.rotate(Math.atan2(uy, ux));
    ctx.fillStyle = NAIL;
    ctx.beginPath();
    ctx.ellipse(0, 0, wTip * 0.34, wTip * 0.26, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(150, 100, 70, 0.35)";
    ctx.lineWidth = 0.8;
    ctx.stroke();
    ctx.restore();

    // press glow — subtle ring, no colour change on the skin
    if (f.press > 0.12) {
      ctx.strokeStyle = `rgba(77, 255, 176, ${0.25 + 0.45 * f.press})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(tx, ty, wTip * 0.75 + f.press * 3, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  private badge(tx: number, ty: number, f: FingerSpec): void {
    const ctx = this.ctx;
    const by = ty - 17;
    ctx.fillStyle = f.highlight ? ACCENT : "rgba(8, 20, 16, 0.88)";
    ctx.strokeStyle = f.highlight ? "rgba(6, 32, 24, 0.9)" : "rgba(77, 255, 176, 0.75)";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(tx, by, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = f.highlight ? "#062018" : ACCENT;
    ctx.font = "700 11px Sora, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(f.num), tx, by + 0.5);
  }

  /* ———— HUD ———— */

  private caption(w: number, h: number, text: string, sub?: string): void {
    const ctx = this.ctx;
    const barH = sub ? 46 : 34;
    const y = h - barH - 8;
    ctx.fillStyle = "rgba(4, 12, 16, 0.78)";
    ctx.beginPath();
    ctx.roundRect(12, y, w - 24, barH, 10);
    ctx.fill();
    ctx.fillStyle = INK;
    ctx.font = `600 ${Math.max(12.5, Math.min(15.5, w * 0.025))}px Sora, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, w / 2, y + (sub ? 16 : barH / 2));
    if (sub) {
      ctx.fillStyle = MUTED;
      ctx.font = `500 ${Math.max(10.5, Math.min(12.5, w * 0.019))}px Sora, sans-serif`;
      ctx.fillText(sub, w / 2, y + 33);
    }
  }

  private stepDots(w: number, n: number, active: number): void {
    const ctx = this.ctx;
    const gap = 10;
    const r = 4.5;
    const total = n * r * 2 + (n - 1) * gap;
    let x = (w - total) / 2 + r;
    for (let i = 0; i < n; i++) {
      ctx.beginPath();
      ctx.fillStyle = i === active ? ACCENT : "rgba(255,255,255,0.22)";
      ctx.arc(x, 16, i === active ? r + 1.2 : r, 0, Math.PI * 2);
      ctx.fill();
      x += r * 2 + gap;
    }
  }

  private keyLabel(x: number, y: number, text: string, color = "rgba(26,22,18,0.55)"): void {
    const ctx = this.ctx;
    ctx.fillStyle = color;
    ctx.font = "700 11px Sora, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, x, y);
  }

  /* ———— scenes ———— */

  private scenePosture(w: number, h: number, t: number): void {
    const ctx = this.ctx;
    const phase = (t % 6.5) / 6.5;
    const good = ease(clamp01((phase - 0.2) * 2.8));
    const cx = w * 0.4;
    const floor = h * 0.8;

    // floor line
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(w * 0.06, floor);
    ctx.lineTo(w * 0.94, floor);
    ctx.stroke();

    // upright piano, side view
    const pnoG = ctx.createLinearGradient(cx + 88, floor - 160, cx + 240, floor);
    pnoG.addColorStop(0, "#2e2118");
    pnoG.addColorStop(1, "#1b130c");
    ctx.fillStyle = pnoG;
    ctx.beginPath();
    ctx.roundRect(cx + 88, floor - 160, 150, 160, [8, 8, 0, 0]);
    ctx.fill();
    // key bed sticking out, with visible keys
    ctx.fillStyle = "#241a11";
    ctx.fillRect(cx + 60, floor - 102, 34, 14);
    ctx.fillStyle = KEY_WHITE_TOP;
    ctx.fillRect(cx + 60, floor - 99, 30, 7);
    ctx.strokeStyle = "rgba(30, 22, 14, 0.5)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(cx + 60 + i * 6, floor - 99);
      ctx.lineTo(cx + 60 + i * 6, floor - 92);
      ctx.stroke();
    }
    // music stand hint
    ctx.fillStyle = "rgba(255, 246, 230, 0.1)";
    ctx.fillRect(cx + 96, floor - 136, 34, 24);

    // bench
    ctx.fillStyle = "#3a2b1c";
    ctx.beginPath();
    ctx.roundRect(cx - 72, floor - 56, 86, 9, 2);
    ctx.fill();
    ctx.fillStyle = "#221809";
    ctx.fillRect(cx - 64, floor - 47, 6, 47);
    ctx.fillRect(cx - 6, floor - 47, 6, 47);

    // ——— seated figure (smooth filled silhouette) ———
    const slump = (1 - good) * 0.5;
    const hipX = cx - 36;
    const hipY = floor - 60;
    const spineAng = -Math.PI / 2 + slump * 0.52;
    const backLen = 88;
    const shX = hipX + Math.cos(spineAng) * backLen;
    const shY = hipY + Math.sin(spineAng) * backLen;
    const headX = shX + Math.cos(spineAng + slump * 0.35) * 26;
    const headY = shY + Math.sin(spineAng + slump * 0.35) * 26;

    // legs (thigh + shin, filled)
    ctx.fillStyle = "#243731";
    ctx.beginPath();
    ctx.moveTo(hipX - 8, hipY - 8);
    ctx.quadraticCurveTo(hipX + 26, hipY - 14, hipX + 52, hipY + 2);
    ctx.lineTo(hipX + 56, hipY + 16);
    ctx.quadraticCurveTo(hipX + 26, hipY + 8, hipX - 6, hipY + 10);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.roundRect(hipX + 44, hipY + 2, 13, floor - hipY - 2, 5);
    ctx.fill();
    // shoe
    ctx.fillStyle = "#141a17";
    ctx.beginPath();
    ctx.roundRect(hipX + 42, floor - 8, 30, 8, [2, 6, 2, 2]);
    ctx.fill();

    // torso — curved back silhouette
    const backBow = slump * 26; // curvature of a slumped back
    ctx.fillStyle = "#2f463c";
    ctx.beginPath();
    ctx.moveTo(hipX - 12, hipY + 6);
    ctx.quadraticCurveTo(
      (hipX + shX) / 2 - 14 - backBow,
      (hipY + shY) / 2,
      shX - 8,
      shY - 4,
    );
    ctx.quadraticCurveTo(shX + 2, shY - 10, shX + 12, shY - 2);
    ctx.lineTo(shX + 14, shY + 12);
    ctx.quadraticCurveTo((hipX + shX) / 2 + 8 - backBow * 0.4, (hipY + shY) / 2 + 10, hipX + 12, hipY + 8);
    ctx.closePath();
    ctx.fill();
    // rim light along the back
    ctx.strokeStyle = good > 0.55 ? "rgba(77,255,176,0.5)" : "rgba(240,193,90,0.45)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(hipX - 10, hipY + 4);
    ctx.quadraticCurveTo((hipX + shX) / 2 - 14 - backBow, (hipY + shY) / 2, shX - 6, shY - 6);
    ctx.stroke();

    // arm to keys — upper arm + forearm
    ctx.strokeStyle = "#37514a";
    ctx.lineCap = "round";
    ctx.lineWidth = 11;
    ctx.beginPath();
    ctx.moveTo(shX + 4, shY + 8);
    ctx.lineTo(shX + 36, shY + 34 - good * 6);
    ctx.stroke();
    ctx.strokeStyle = SKIN_BASE;
    ctx.lineWidth = 9;
    ctx.beginPath();
    ctx.moveTo(shX + 36, shY + 34 - good * 6);
    ctx.lineTo(cx + 68, floor - 94);
    ctx.stroke();
    ctx.fillStyle = SKIN_BASE;
    ctx.beginPath();
    ctx.arc(cx + 70, floor - 94, 5, 0, Math.PI * 2);
    ctx.fill();

    // head + neck
    ctx.fillStyle = SKIN_BASE;
    ctx.beginPath();
    ctx.arc(headX, headY, 14.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#20302a";
    ctx.beginPath(); // hair
    ctx.arc(headX - 2, headY - 4, 14, Math.PI * 0.85, Math.PI * 1.95);
    ctx.quadraticCurveTo(headX + 10, headY - 16, headX + 12, headY - 4);
    ctx.closePath();
    ctx.fill();

    // plumb line (ideal upright)
    ctx.strokeStyle = "rgba(255,255,255,0.14)";
    ctx.setLineDash([4, 6]);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(hipX, floor - 190);
    ctx.lineTo(hipX, hipY + 6);
    ctx.stroke();
    ctx.setLineDash([]);

    // status chip
    ctx.fillStyle = good > 0.55 ? ACCENT : WRONG;
    ctx.beginPath();
    ctx.arc(w * 0.8, h * 0.16, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = INK;
    ctx.font = "600 12px Sora, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(good > 0.55 ? "ALIGNED" : "SLUMPED", w * 0.8 + 12, h * 0.16);

    this.caption(
      w,
      h,
      good > 0.55 ? "Tall spine · soft shoulders" : "Sit tall — don’t slump",
      "Front half of the bench · forearms level with the keys",
    );
  }

  /** RH: 1→C 2→D 3→E 4→F 5→G. LH: 5→C 4→D 3→E 2→F 1→G. */
  private sceneFivePos(w: number, h: number, t: number, leftHand: boolean): void {
    const cKey = leftHand ? 2 : 3; // keep hand centred
    const spec: KeyboardSpec = {
      x: w * 0.06,
      y: h * 0.12,
      w: w * 0.88,
      h: h * 0.34,
      whiteCount: 12,
      patternOffset: (7 - (cKey % 7)) % 7, // black keys align so cKey is a real C
    };
    const step = Math.floor(t / 1.2) % 5;
    const press = pulse(t, 1.2);

    const nums = leftHand ? [5, 4, 3, 2, 1] : [1, 2, 3, 4, 5];
    const pressed = new Map<number, string>();
    pressed.set(cKey + step, "down");
    this.keyboard(spec, pressed);

    const letters = ["C", "D", "E", "F", "G"];
    for (let i = 0; i < 5; i++) {
      this.keyLabel(
        this.keyCenterX(spec, cKey + i),
        spec.y + spec.h - 10,
        letters[i],
        i === step ? "#0a3d2a" : "rgba(26,22,18,0.5)",
      );
    }

    const fingers: FingerSpec[] = [];
    for (let i = 0; i < 5; i++) {
      fingers.push({
        key: cKey + i,
        num: nums[i],
        press: i === step ? press : 0,
        highlight: i === step,
      });
    }
    this.handOnKeys(spec, leftHand, fingers);

    this.stepDots(w, 5, step);
    this.caption(
      w,
      h,
      `Finger ${nums[step]} plays ${letters[step]}`,
      leftHand
        ? "Left hand mirrors — pinky (5) on low C · thumb (1) on G"
        : "Thumb (1) on Middle C · one finger per key · soft curve",
    );
  }

  private sceneDirection(w: number, h: number, t: number): void {
    const spec: KeyboardSpec = { x: w * 0.05, y: h * 0.14, w: w * 0.9, h: h * 0.32, whiteCount: 14 };
    const period = 5.6;
    const p = (t % period) / period;
    const ascending = p < 0.5;
    const local = ease((p % 0.5) * 2);
    const pos = ascending ? local : 1 - local;

    const start = 1 + Math.round(pos * 7);
    const walking = Math.floor(((t * 2.2) % 5 + 5) % 5);
    const pressed = new Map<number, string>([[start + walking, "down"]]);
    this.keyboard(spec, pressed);

    const ctx = this.ctx;
    const ay = spec.y - 22;
    ctx.strokeStyle = ascending ? ACCENT : GOLD;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(spec.x + spec.w * 0.2, ay);
    ctx.lineTo(spec.x + spec.w * 0.8, ay);
    ctx.stroke();
    const tipX = ascending ? spec.x + spec.w * 0.8 : spec.x + spec.w * 0.2;
    const dir = ascending ? 1 : -1;
    ctx.beginPath();
    ctx.moveTo(tipX, ay);
    ctx.lineTo(tipX - 9 * dir, ay - 5);
    ctx.moveTo(tipX, ay);
    ctx.lineTo(tipX - 9 * dir, ay + 5);
    ctx.stroke();

    const fingers: FingerSpec[] = [];
    for (let i = 0; i < 5; i++) {
      fingers.push({
        key: start + i,
        num: i + 1,
        press: i === walking ? 0.8 : 0,
        highlight: i === walking,
      });
    }
    this.handOnKeys(spec, false, fingers);

    this.caption(
      w,
      h,
      ascending ? "Ascending — walk left → right" : "Descending — walk right → left",
      "Forearm travels with the hand · wrist stays quiet",
    );
  }

  private sceneThumbTuck(w: number, h: number, t: number): void {
    const c = 2; // C D E F G A at 2..7
    const spec: KeyboardSpec = {
      x: w * 0.06,
      y: h * 0.13,
      w: w * 0.88,
      h: h * 0.32,
      whiteCount: 12,
      patternOffset: (7 - (c % 7)) % 7,
    };
    const period = 7.2;
    const p = (t % period) / period;
    const step = Math.min(3, Math.floor(p * 4));
    const local = ease((p * 4) % 1);

    const pressed = new Map<number, string>();
    let fingers: FingerSpec[];

    if (step === 0) {
      const fi = Math.min(2, Math.floor(local * 2.99));
      pressed.set(c + fi, "down");
      fingers = [
        { key: c, num: 1, press: fi === 0 ? 0.9 : 0, highlight: fi === 0 },
        { key: c + 1, num: 2, press: fi === 1 ? 0.9 : 0, highlight: fi === 1 },
        { key: c + 2, num: 3, press: fi === 2 ? 0.9 : 0, highlight: fi === 2 },
        { key: c + 3, num: 4, press: 0, highlight: false },
        { key: c + 4, num: 5, press: 0, highlight: false },
      ];
    } else if (step === 1) {
      pressed.set(c + 2, "down");
      const tuck = local;
      fingers = [
        { key: c, num: 1, press: 0, highlight: true, ox: tuck * 3, oy: 14 + tuck * 10 },
        { key: c + 1, num: 2, press: 0.2, highlight: false },
        { key: c + 2, num: 3, press: 0.55, highlight: false },
        { key: c + 3, num: 4, press: 0, highlight: false },
        { key: c + 4, num: 5, press: 0, highlight: false },
      ];
      const ctx = this.ctx;
      ctx.strokeStyle = GOLD;
      ctx.setLineDash([4, 5]);
      ctx.lineWidth = 2;
      ctx.beginPath();
      const y0 = spec.y + spec.h * 0.82;
      ctx.moveTo(this.keyCenterX(spec, c), y0 + 10);
      ctx.quadraticCurveTo(this.keyCenterX(spec, c + 1.5), y0 + 40, this.keyCenterX(spec, c + 3), y0 + 8);
      ctx.stroke();
      ctx.setLineDash([]);
    } else if (step === 2) {
      pressed.set(c + 3, "down");
      fingers = [
        { key: c + 3, num: 1, press: 0.95, highlight: true },
        { key: c + 4, num: 2, press: 0, highlight: false },
        { key: c + 5, num: 3, press: 0, highlight: false },
        { key: c + 6, num: 4, press: 0, highlight: false },
        { key: c + 7, num: 5, press: 0, highlight: false },
      ];
    } else {
      const fi = Math.min(2, Math.floor(local * 2.99));
      pressed.set(c + 3 + fi, "down");
      fingers = [
        { key: c + 3, num: 1, press: fi === 0 ? 0.9 : 0, highlight: fi === 0 },
        { key: c + 4, num: 2, press: fi === 1 ? 0.9 : 0, highlight: fi === 1 },
        { key: c + 5, num: 3, press: fi === 2 ? 0.9 : 0, highlight: fi === 2 },
        { key: c + 6, num: 4, press: 0, highlight: false },
        { key: c + 7, num: 5, press: 0, highlight: false },
      ];
    }

    this.keyboard(spec, pressed);
    const names = ["C", "D", "E", "F", "G", "A"];
    for (let i = 0; i < 6; i++) {
      this.keyLabel(this.keyCenterX(spec, c + i), spec.y + spec.h - 10, names[i]);
    }
    this.handOnKeys(spec, false, fingers);

    this.stepDots(w, 4, step);
    const subs = [
      "Fingers 1–2–3 play C · D · E",
      "Thumb (1) slides UNDER toward F — wrist quiet",
      "Thumb lands on F — new hand position",
      "Continue the scale from the new shape",
    ];
    this.caption(w, h, `Step ${step + 1} / 4`, subs[step]);
  }

  private sceneBlackKeys(w: number, h: number, t: number): void {
    const spec: KeyboardSpec = { x: w * 0.05, y: h * 0.18, w: w * 0.9, h: h * 0.46, whiteCount: 14 };
    const phase = Math.floor(t / 2.4) % 2;
    const glow = pulse(t, 2.4);

    const blackPressed = new Map<number, string>();
    for (let base = 0; base < spec.whiteCount - 1; base += 7) {
      if (phase === 0) {
        blackPressed.set(base + 0, KEY_BLACK_DOWN);
        blackPressed.set(base + 1, KEY_BLACK_DOWN);
      } else {
        blackPressed.set(base + 3, KEY_BLACK_DOWN);
        blackPressed.set(base + 4, KEY_BLACK_DOWN);
        blackPressed.set(base + 5, KEY_BLACK_DOWN);
      }
    }
    this.keyboard(spec, new Map(), blackPressed);

    const ctx = this.ctx;
    const kw = this.kw(spec);
    ctx.strokeStyle = `rgba(77, 255, 176, ${0.3 + glow * 0.55})`;
    ctx.lineWidth = 2.2;
    if (phase === 0) {
      ctx.strokeRect(spec.x + 0.55 * kw, spec.y - 8, kw * 2, spec.h * 0.68);
      this.keyLabel(spec.x + 1.55 * kw, spec.y - 18, "TWO black keys", ACCENT);
      this.keyLabel(this.keyCenterX(spec, 0), spec.y + spec.h - 11, "C", "#7a4a12");
      this.keyLabel(spec.x + 1.05 * kw, spec.y + spec.h * 0.68 + 12, "C♯", GOLD);
      this.keyLabel(spec.x + 2.05 * kw, spec.y + spec.h * 0.68 + 12, "E♭", GOLD);
    } else {
      ctx.strokeRect(spec.x + 3.55 * kw, spec.y - 8, kw * 3, spec.h * 0.68);
      this.keyLabel(spec.x + 5.05 * kw, spec.y - 18, "THREE black keys", ACCENT);
      this.keyLabel(this.keyCenterX(spec, 3), spec.y + spec.h - 11, "F", "#7a4a12");
      this.keyLabel(spec.x + 4.05 * kw, spec.y + spec.h * 0.68 + 12, "F♯", GOLD);
      this.keyLabel(spec.x + 6.05 * kw, spec.y + spec.h * 0.68 + 12, "B♭", GOLD);
    }

    this.stepDots(w, 2, phase);
    this.caption(
      w,
      h,
      phase === 0 ? "Two-black group — C sits just to its left" : "Three-black group — F sits just to its left",
      "Black-key groups are your landmarks for finding any note",
    );
  }

  private sceneChord(w: number, h: number, t: number): void {
    // C at white index 2 → offset aligns the black-key pattern to the labels
    const spec: KeyboardSpec = {
      x: w * 0.06,
      y: h * 0.12,
      w: w * 0.88,
      h: h * 0.34,
      whiteCount: 12,
      patternOffset: 5,
    };
    const step = Math.floor(t / 2.2) % 3;
    const press = pulse(t, 2.2);

    const shapes: Array<{ keys: number[]; nums: number[]; label: string; notes: string[] }> = [
      { keys: [2, 4, 6], nums: [1, 3, 5], label: "Root position — C · E · G", notes: ["C", "E", "G"] },
      { keys: [4, 6, 9], nums: [1, 2, 5], label: "1st inversion — E · G · C", notes: ["E", "G", "C"] },
      { keys: [6, 9, 11], nums: [1, 3, 5], label: "2nd inversion — G · C · E", notes: ["G", "C", "E"] },
    ];
    const shape = shapes[step];
    const pressed = new Map<number, string>();
    if (press > 0.3) for (const k of shape.keys) pressed.set(k, "down");
    this.keyboard(spec, pressed);

    for (let i = 0; i < 3; i++) {
      this.keyLabel(this.keyCenterX(spec, shape.keys[i]), spec.y + spec.h - 10, shape.notes[i], "#0a3d2a");
    }

    // physical L→R slots — chord fingers press; others hover lifted
    const fingers: FingerSpec[] = [];
    for (let slot = 0; slot < 5; slot++) {
      const num = slot + 1;
      const idx = shape.nums.indexOf(num);
      if (idx >= 0) {
        fingers.push({ key: shape.keys[idx], num, press, highlight: true });
      } else {
        // hover between the chord tones this finger sits over
        const lo = shape.keys[0];
        const hi = shape.keys[2];
        const hoverKey = Math.round(lerp(lo, hi, slot / 4));
        fingers.push({ key: hoverKey, num, press: 0, highlight: false, oy: 12 });
      }
    }
    this.handOnKeys(spec, false, fingers);

    this.stepDots(w, 3, step);
    this.caption(w, h, `${shape.label}  (fingers ${shape.nums.join(" · ")})`, "All chord tones land together — one shape, one attack");
  }

  private sceneBlind(w: number, h: number, t: number): void {
    const spec: KeyboardSpec = { x: w * 0.05, y: h * 0.16, w: w * 0.9, h: h * 0.44, whiteCount: 14 };
    this.keyboard(spec);

    const ctx = this.ctx;
    const veil = 0.5 + 0.16 * Math.sin(t * 0.75);
    ctx.fillStyle = `rgba(6, 12, 10, ${veil})`;
    ctx.fillRect(spec.x - 8, spec.y - 14, spec.w + 16, spec.h + 22);

    const kw = this.kw(spec);
    const glow = pulse(t, 2.5);
    const bw = kw * 0.58;
    const bh = spec.h * 0.6;

    ctx.fillStyle = `rgba(77, 255, 176, ${0.22 + glow * 0.4})`;
    for (const i of [0, 1, 7, 8]) {
      ctx.beginPath();
      ctx.roundRect(spec.x + (i + 1) * kw - bw / 2, spec.y, bw, bh, 3);
      ctx.fill();
    }
    ctx.fillStyle = `rgba(240, 193, 90, ${0.18 + glow * 0.35})`;
    for (const i of [3, 4, 5, 10, 11, 12]) {
      ctx.beginPath();
      ctx.roundRect(spec.x + (i + 1) * kw - bw / 2, spec.y, bw, bh, 3);
      ctx.fill();
    }

    this.keyLabel(spec.x + 1.5 * kw, spec.y - 14, "2-black", ACCENT);
    this.keyLabel(spec.x + 5 * kw, spec.y - 14, "3-black", GOLD);

    // hand drifts, then thumb finds C beside the two-black group
    const find = ease((Math.sin(t * 0.55) + 1) / 2);
    const cKey = Math.round(lerp(7, 0, find));
    const fingers: FingerSpec[] = [
      { key: cKey, num: 1, press: find > 0.8 ? 0.7 : 0.1, highlight: true },
      { key: cKey + 1, num: 2, press: 0, highlight: false },
      { key: cKey + 2, num: 3, press: 0, highlight: false },
      { key: cKey + 3, num: 4, press: 0, highlight: false },
      { key: cKey + 4, num: 5, press: 0, highlight: false },
    ];
    this.handOnKeys(spec, false, fingers);

    this.caption(
      w,
      h,
      "Eyes off — feel the landmarks",
      "Find C by the two-black group · slow accuracy before speed",
    );
  }
}
